<template>
  <toggle-section
    section="corporateRecord"
    chevron-position="right"
    :has-padding="false"
    :is-open="isOpen.corporateRecord"
    content-gray
    class="collapse-toggle-padding"
    data-cy="corporate-record-section"
    @toggle="toggle"
  >
    <template #titleSection>
      <h3 class="font-semibold text-headline">
        {{ t("DEALS.CLEAR_DOCUMENT_RETRIEVAL.CORPORATE_RECORD") }}
      </h3>
    </template>
    <template #content>
      <div class="space-y-6 p-6">
        <toggle-section
          section="businessInfo"
          :is-open="isOpen.businessInfo"
          chevron-position="right"
          :has-padding="false"
          class="collapse-toggle-padding bg-white"
          @toggle="toggle"
        >
          <template #titleSection>
            <h4 class="text-headline">
              {{ t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.TITLE") }}
            </h4>
          </template>
          <template #content>
            <div class="space-y-4 p-4">
              <value-row
                v-for="item in businessInfo"
                :key="item.label"
                :item="item"
              />
            </div>
          </template>
        </toggle-section>
        <div class="flex w-full space-x-2 rounded-sm border bg-white px-4 py-3">
          <div class="w-1/5 text-headline">
            {{ t("DEALS.CLEAR_DOCUMENT_RETRIEVAL.BUSINESS_TYPE") }}
          </div>
          <div class="value text-headline">
            {{ data?.BusinessType || "-" }}
          </div>
        </div>
        <toggle-section
          section="corpAgentInfo"
          :is-open="isOpen.corpAgentInfo"
          chevron-position="right"
          :has-padding="false"
          class="collapse-toggle-padding bg-white"
          @toggle="toggle"
        >
          <template #titleSection>
            <h4 class="text-headline">
              {{ t("DEALS.CLEAR_KYB.CORP_AGENT_INFO") }}
            </h4>
          </template>
          <template #content>
            <div class="space-y-4 p-4">
              <value-row
                v-for="item in corpAgentInfo"
                :key="item.label"
                :item="item"
              />
            </div>
          </template>
        </toggle-section>
        <toggle-section
          section="corpOfficerInfo"
          :is-open="isOpen.corpOfficerInfo"
          chevron-position="right"
          :has-padding="false"
          class="collapse-toggle-padding bg-white"
          @toggle="toggle"
        >
          <template #titleSection>
            <h4 class="text-headline">
              {{ t("DEALS.CLEAR_KYB.CORP_OFFICER_INFO") }}
            </h4>
          </template>
          <template #content>
            <div class="space-y-4 p-4">
              <value-row
                v-for="item in corpOfficerInfo"
                :key="item.label"
                :item="item"
              />
            </div>
          </template>
        </toggle-section>
      </div>
    </template>
  </toggle-section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import ToggleSection from "@/components/ToggleSection.vue";
import ValueRow from "@/components/deals/dataServices/components/clear/documentRetrieval/ValueRow.vue";

import type { CorporateFilingItem } from "@/models/commercialData/clear/ClearIdConfirmBusiness";

const props = defineProps<{
  data?: CorporateFilingItem;
}>();

const { t } = useI18n();

const isOpen = ref<Record<string, boolean>>({
  corporateRecord: true,
  businessInfo: true,
  corpAgentInfo: true,
  corpOfficerInfo: true,
});

const businessInfo = computed(() => {
  return [
    {
      label: t("DEALS.CLEAR_KYB.CORP_BUSINESS_STATUS"),
      value: props.data?.BusinessInfo?.CorpBusinessStatus || "-",
    },
    {
      label: t("DEALS.CLEAR_KYB.FILING_DATE"),
      value: props.data?.BusinessInfo?.FilingDate || "-",
    },
    {
      label: t("DEALS.CLEAR_KYB.FILING_STATE"),
      value: props.data?.BusinessInfo?.FilingState || "-",
    },
    {
      label: t("DEALS.CLEAR_KYB.STATE_OF_INCORPORATION"),
      value: props.data?.BusinessInfo?.StateOfIncorporation || "-",
    },
    {
      label: t("COMMON.BUSINESS_NAME"),
      value: props.data?.BusinessInfo?.BusinessName || "-",
      class: "border-b pb-4",
    },
    {
      label: t("COMMON.STREET"),
      value:
        props.data?.BusinessInfo?.FilingOfficeAddress?.Address?.Street || "-",
    },
    {
      label: t("COMMON.CITY"),
      value:
        props.data?.BusinessInfo?.FilingOfficeAddress?.Address?.City || "-",
    },
    {
      label: t("COMMON.STATE"),
      value:
        props.data?.BusinessInfo?.FilingOfficeAddress?.Address?.State || "-",
    },
    {
      label: t("COMMON.ZIP"),
      value:
        props.data?.BusinessInfo?.FilingOfficeAddress?.Address?.ZipCode || "-",
      class: "border-b pb-4",
    },
    {
      label: t("DEALS.CLEAR_KYB.FILING_OFFICE"),
      value:
        props.data?.BusinessInfo?.FilingOfficeAddress?.FilingOfficeName || "-",
    },
    {
      label: t("DEALS.CLEAR_KYB.FILING_STATE"),
      value: props.data?.BusinessInfo?.FilingOfficeAddress?.FilingState || "-",
    },
  ];
});

const corpAgentInfo = computed(() => {
  return [
    {
      label: t("COMMON.STREET"),
      value: props.data?.CorpAgentInfo?.CorpAgentAddress?.StreetLine1 || "-",
    },
    {
      label: t("COMMON.CITY"),

      value: props.data?.CorpAgentInfo?.CorpAgentAddress?.City || "-",
    },
    {
      label: t("COMMON.STATE"),

      value: props.data?.CorpAgentInfo?.CorpAgentAddress?.State || "-",
    },
    {
      label: t("COMMON.ZIP"),

      value: props.data?.CorpAgentInfo?.CorpAgentAddress?.ZipCode || "-",
      class: "border-b pb-4",
    },
    {
      label: t("COMMON.FULL_NAME"),
      value: props.data?.CorpAgentInfo?.PersonName?.FullName || "-",
    },
  ];
});

const corpOfficerInfo = computed(() => {
  return [
    {
      label: t("COMMON.STREET"),
      value:
        props.data?.CorpOfficerInfo?.CorpOfficerAddress?.StreetLine1 || "-",
    },
    {
      label: t("COMMON.CITY"),

      value: props.data?.CorpOfficerInfo?.CorpOfficerAddress?.City || "-",
    },
    {
      label: t("COMMON.STATE"),

      value: props.data?.CorpOfficerInfo?.CorpOfficerAddress?.State || "-",
    },
    {
      label: t("COMMON.ZIP"),

      value: props.data?.CorpOfficerInfo?.CorpOfficerAddress?.ZipCode || "-",
      class: "border-b pb-4",
    },
    {
      label: t("DEALS.CLEAR_KYB.NAME_TYPE_DESC"),
      value: props.data?.CorpOfficerInfo?.NameTypeDesc || "-",
    },
    {
      label: t("COMMON.FULL_NAME"),
      value: props.data?.CorpOfficerInfo?.PersonName?.FullName || "-",
      class: "border-b pb-4",
    },
    {
      label: t("DEALS.CLEAR_KYB.EFFECTIVE_DATE"),
      value: props.data?.EffectiveDate || "-",
    },
    {
      label: t("COMMON.NOTE"),
      value: props.data?.Note || "-",
      class: "border-b pb-4",
    },
    {
      label: t("DEALS.CLEAR_KYB.SOURCE"),
      value: props.data?.Source || "-",
    },
    {
      label: t("DEALS.CLEAR_KYB.SOURCE_DOCUMENT_GUID"),
      value: props.data?.SourceDocumentGuid || "-",
    },
  ];
});

const toggle = (section: string) => {
  isOpen.value[section] = !isOpen.value[section];
};
</script>
